import { useMemo } from 'react';
import { ConfigurationGroupDeviceType } from '@casops/store/apis/commonModels';
import i18n from '@casops/i18n';
import { ValueGetterFunc, ValueGetterParams } from 'ag-grid-community';

export enum DeviceType {
  Unknown = 'Unknown',
  X1 = 'X1',
  X9 = 'X9',
  IVU = 'IVU',
  IVUPlus = 'IVUPlus',
  PPU = 'PPU',
  Generic = 'Generic',
}

export const compatibleDevicesMap: Record<ConfigurationGroupDeviceType, DeviceType[]> = {
  IVU: [DeviceType.IVU, DeviceType.IVUPlus], // Gen 2 can run on IVU or IVU+
  IVUPlus: [DeviceType.IVUPlus],
  Generic: [DeviceType.Generic],
  X1: [DeviceType.X1],
  X9: [DeviceType.X9],
};

const getCompatibleDevicesLabels = (
  deviceType: ConfigurationGroupDeviceType,
  isVerbose = false,
) => {
  return compatibleDevicesMap[deviceType]
    .map(dt => {
      // Enable more detailed 'Any' labels for contexts where both software and device labels are shown together.
      if (dt === DeviceType.Generic && isVerbose) {
        return i18n.t('AnyDevice');
      } else {
        return i18n.t(`DeviceType.${dt}`);
      }
    })
    .sort((a, b) => a.localeCompare(b));
};

export const useCompatibleDevicesLabels = (
  deviceType?: ConfigurationGroupDeviceType,
  isVerbose?: boolean,
): string[] => {
  const deviceTypeLabels = useMemo<string[]>(() => {
    return typeof deviceType !== 'undefined'
      ? getCompatibleDevicesLabels(deviceType, isVerbose)
      : [];
  }, [deviceType, isVerbose]);

  return deviceTypeLabels;
};

export const compatibleDevicesValueGetter = <TData extends { hw: ConfigurationGroupDeviceType }>({
  data,
}: ValueGetterParams<TData, string[]>): ReturnType<ValueGetterFunc<TData, string[]>> => {
  return typeof data?.hw === 'string' ? getCompatibleDevicesLabels(data.hw) : [];
};
